import { Core, Decorative } from "@faire/design-tokens";
import { useNationality } from "@faire/retailer-visitor-shared/hooks/retailer/useNationality";
import {
  EUROPE_NEWS_ARTICLES_GROUP,
  IEuropeNewsArticle,
} from "@faire/retailer-visitor-shared/views/brands/landing-page/const";
import { optimizeImageUrlHeight } from "@faire/web--source/common/images/optimizeImages";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Color } from "@faire/web--source/slate/Color";
import { Column, Flex, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { SpacerMultipliersKey } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { EmbeddedLink } from "@faire/web--source/ui/EmbeddedLink";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { ResponsiveContent } from "@faire/web--source/ui/ResponsiveContent";
import { trackBrandLeadsNewsSectionLinkedArticlesClick } from "@faire/web-api--source/events/brandLeads/click/newsSectionLinkedArticles";
import React from "react";
import styled from "styled-components";

export const BrandsLandingPageNews: React.FC<{ onView?: () => void }> = ({
  onView,
}) => {
  const viewRef = useDoActionWhenVisible(onView);
  const { retailerCountry } = useNationality();
  const country: string = retailerCountry ?? "";

  // @ts-expect-error FIXME(implicitAny): https://faire.link/no-implicit-any
  const newsInfo: IEuropeNewsArticle[] = EUROPE_NEWS_ARTICLES_GROUP[country];

  const backgroundColor: Color =
    country === "FRA" ? Core.surface.inverse : Decorative.tint.neutral;

  const mobileNewsTopGapSize: SpacerMultipliersKey =
    country === "FRA" ? "3x" : "4x";

  const isSSR = useIsSSR();

  if (!newsInfo) {
    return null;
  }

  return (
    <Wrapper
      ref={onView ? viewRef : undefined}
      $backgroundColor={backgroundColor}
    >
      <NewsArticleWrapper $mobileNewsTopGapSize={mobileNewsTopGapSize}>
        <ResponsiveContent
          mobile={
            <>
              <Typography variant="pageHeaderSerifRegular">
                <StrictLocalMsg
                  defaultMessage="In the News"
                  description={{
                    text: "Gives information on third-party news reports",
                  }}
                />
              </Typography>
              <Spacer size="3x" />
            </>
          }
          desktop={null}
        />
        {newsInfo.map((news, index) => (
          <NewsColumn $index={index} key={index}>
            <LogoRow>
              <LogoImage
                src={optimizeImageUrlHeight(
                  { url: news.mediaLogoImageUrl },
                  isSSR,
                  news.mediaLogoHeight
                )}
                height={news.mediaLogoHeight}
              />
            </LogoRow>
            <ResponsiveContent
              desktop={
                <NewsDescription>{news.mediaArticleOverview}</NewsDescription>
              }
              mobile={
                <NewsDescription>
                  {news.mediaArticleOverviewMobile ?? news.mediaArticleOverview}
                </NewsDescription>
              }
            />
            <Spacer height="1x" />
            <EmbeddedLink
              linkTo={news.mediaArticleUrl}
              target="_blank"
              onClick={() => {
                trackBrandLeadsNewsSectionLinkedArticlesClick();
              }}
            >
              <Typography
                variant="paragraphSansRegular"
                style={{ whiteSpace: "nowrap" }}
              >
                <StrictLocalMsg
                  defaultMessage="Read Article"
                  description={{ text: "Opens the link to the news article" }}
                />
              </Typography>
            </EmbeddedLink>
            <Spacer size="4x" />
          </NewsColumn>
        ))}
      </NewsArticleWrapper>
    </Wrapper>
  );
};

const Wrapper = styled(Column).attrs({ align: "center", justify: "center" })<{
  $backgroundColor?: Color;
}>`
  height: 404px;
  width: 100%;

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing(0, "9x")};
    max-width: 1008px;
    min-width: 780px;
  }

  ${responsiveMediaQueries.mobile} {
    height: fit-content;
    background-color: ${(props) =>
      props.$backgroundColor ?? Decorative.tint.neutral};
  }
`;

const NewsArticleWrapper = styled(Flex).attrs({
  justify: "space-around",
  direction: { mobileAndAbove: "column", tabletAndAbove: "row" },
})<{
  $mobileNewsTopGapSize?: SpacerMultipliersKey;
}>`
  width: 100%;
  ${responsiveMediaQueries.mobile} {
    padding: ${(props) =>
      getSpacing(props.$mobileNewsTopGapSize ?? "4x", "3x", "1x", "3x")};
  }
`;

const NewsColumn = styled(Column).attrs({
  align: { mobileAndAbove: "flex-start", tabletAndAbove: "center" },
  justify: { mobileAndAbove: "flex-start", tabletAndAbove: "normal" },
})<{ $index: number }>`
  ${responsiveMediaQueries.tabletAndAbove} {
    opacity: 0;
    animation: fadeIn 0.5s ease 1 forwards;
    animation-delay: ${(props) => 0.5 * props.$index}s;

    @keyframes fadeIn {
      to {
        opacity: 1;
      }
    }
    margin: ${getSpacing(0, "1x")};
  }

  ${responsiveMediaQueries.mobile} {
    width: fit-content;
    height: fit-content;
  }
`;

const NewsDescription = styled(Typography)`
  ${responsiveMediaQueries.desktopAndAbove} {
    text-align: center;
    max-width: 272px;
    min-width: 272px;
  }

  ${responsiveMediaQueries.tablet} {
    text-align: center;
    max-width: 228px;
    min-width: 228px;
  }

  ${responsiveMediaQueries.mobile} {
    width: fit-content;
    text-align: start;
    align-items: center;
  }
`;

const LogoRow = styled(Row).attrs({ justify: "center", align: "center" })`
  ${responsiveMediaQueries.tabletAndAbove} {
    height: 48px;
  }
`;

const LogoImage = styled.img`
  margin-bottom: 12px;
`;
